import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * QNA 컨텐츠 목록 템플릿 훅 Props
 */
interface UseListProps {
  filters: any;
  selectedIds: number[];
  onSearch: (filters: any) => void;
  onFilter: (filters: any) => void;
}

/**
 * QNA 컨텐츠 목록 템플릿 커스텀 훅
 *
 * @description 도장 목록 템플릿 useList를 참고하여 구현
 * @param props UseListProps
 * @returns QNA 컨텐츠 목록 템플릿 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
function useList(props: UseListProps) {
  const { filters, selectedIds, onSearch, onFilter } = props;
  const [searchParams, setSearchParams] = useSearchParams();

  // 로컬 상태
  const [contentRequest, setContentRequest] = useState({
    category: filters.category || "",
    keyword: filters.keyword || "",
    useYn:
      filters.useYn === "ALL" ||
      filters.useYn === null ||
      filters.useYn === undefined
        ? ""
        : filters.useYn,
  });

  const [rowsPerPage, setRowsPerPage] = useState<number>(
    parseInt(searchParams.get("limit") || "20")
  );

  /**
   * 페이지 변경 핸들러
   */
  const handlePageChange = useCallback(
    (_: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
      setSearchParams((prev) => {
        prev.set("page", (page + 1).toString()); // MUI는 0-based, API는 1-based
        prev.set("limit", rowsPerPage.toString());
        return new URLSearchParams(prev);
      });
    },
    [rowsPerPage, setSearchParams]
  );

  /**
   * 페이지당 항목 수 변경 핸들러
   */
  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);

      setSearchParams((prev) => {
        prev.set("page", "1"); // 페이지 크기 변경 시 첫 페이지로
        prev.set("limit", newRowsPerPage.toString());
        return new URLSearchParams(prev);
      });
    },
    [setSearchParams]
  );

  /**
   * 선택 변경 핸들러
   */
  const handleSelectionChange = useCallback((newSelectedIds: number[]) => {
    // 이 핸들러는 organism에서 호출되어 상위로 전달됩니다
    // 실제 상태 변경은 상위 컴포넌트에서 처리
  }, []);

  /**
   * 검색 변경 핸들러
   */
  const handleSearchChange = useCallback(
    (newRequest: any) => {
      setContentRequest(newRequest);
      onSearch(newRequest);
    },
    [onSearch]
  );

  /**
   * 필터 변경 핸들러
   */
  const handleFilterChange = useCallback(
    (filterValues: any) => {
      setContentRequest(filterValues);
      onFilter(filterValues);
    },
    [onFilter]
  );

  /**
   * 필터 초기화 핸들러
   */
  const handleResetFilters = useCallback(() => {
    const defaultRequest = {
      category: "",
      keyword: "",
      useYn: "",
    };
    setContentRequest(defaultRequest);
    onSearch(defaultRequest);
  }, [onSearch]);

  return {
    rowsPerPage,
    contentRequest,
    handlePageChange,
    handleRowsPerPageChange,
    handleSelectionChange,
    handleSearchChange,
    handleFilterChange,
    handleResetFilters,
  };
}

export default useList;
